/**
 * Welcome landing (bd bj9).
 *
 * The hosted-checkout root `/` is generic marketing — it is NOT a checkout.
 * It loads no tenant and makes no API call; a real checkout only renders under
 * an explicit `?tenant=…&strategy=…` route.
 *
 * Built entirely from the shared @upg/ui design language: the `upg-eyebrow`
 * global class, the tailwind preset's surface/primary tokens, and the
 * Material Symbols glyphs loaded by `@upg/ui/globals.css`. No parallel design
 * system, no data fetching.
 */
const FEATURES: { icon: string; title: string; body: string }[] = [
  {
    icon: 'bolt',
    title: 'Instant by design',
    body: 'A checkout that loads fast, reads beautifully, and gets out of the way.',
  },
  {
    icon: 'verified_user',
    title: 'Secure by default',
    body: 'PCI-DSS aligned, hosted payment pages, no card data ever touching your stack.',
  },
  {
    icon: 'tune',
    title: 'Configurable everywhere',
    body: 'Branding, methods, copy, and flows — all tuned per tenant, no code changes.',
  },
];

export function Welcome() {
  return (
    <main class="w-full max-w-lg sm:max-w-xl md:max-w-3xl lg:max-w-5xl text-on-surface">
      <section class="text-center md:text-left">
        <p class="upg-eyebrow text-on-primary-container">Universal Payment Gateway</p>
        <h1 class="text-h1 lg:text-[clamp(2rem,3vw,3rem)] font-semibold tracking-tight leading-tight">
          Welcome to the future of payment experiences.
        </h1>
        <p class="text-body-md lg:text-body-lg text-on-surface-variant mt-3 lg:mt-4 max-w-prose md:mx-0 mx-auto">
          One gateway, every method, any tenant — composable, configurable, and conversational. The checkout
          your shoppers actually enjoy.
        </p>
        <div class="mt-6 flex flex-wrap items-center justify-center md:justify-start gap-3 text-on-surface-variant">
          <span class="inline-flex items-center gap-2 text-body-sm">
            <span class="material-symbols-outlined text-[18px]" aria-hidden="true">
              all_inclusive
            </span>
            Multi-provider
          </span>
          <span class="inline-flex items-center gap-2 text-body-sm">
            <span class="material-symbols-outlined text-[18px]" aria-hidden="true">
              public
            </span>
            Global methods
          </span>
          <span class="inline-flex items-center gap-2 text-body-sm">
            <span class="material-symbols-outlined text-[18px]" aria-hidden="true">
              schedule
            </span>
            Real-time
          </span>
        </div>
      </section>

      <section class="mt-8 lg:mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {FEATURES.map((f) => (
          <div
            key={f.title}
            class="bg-surface-container-lowest border border-outline-variant rounded-xl p-5 shadow-sm"
          >
            <span class="material-symbols-outlined text-[22px] text-on-primary-container" aria-hidden="true">
              {f.icon}
            </span>
            <h2 class="text-h3 font-semibold mt-3">{f.title}</h2>
            <p class="text-body-sm text-on-surface-variant mt-1">{f.body}</p>
          </div>
        ))}
      </section>

      <p class="text-[11px] text-on-surface-variant text-center md:text-left mt-8">
        Universal Payment Gateway · {new Date().getFullYear()}
      </p>
    </main>
  );
}
